/**
 * The page registry and its budget — `/v1/admin/seo/*` (docs/05-admin/23 §3.4, backend 40 §4.6).
 *
 * **Every tier write is budget-checked by the server, synchronously.** A promote past the budget
 * answers `409 budget_exceeded` with the headroom arithmetic in its `detail`, and the screen
 * shows that sentence. A client-side check would be a second opinion on a number only the server
 * holds, and the day the two disagreed would be the day a deploy broke.
 *
 * **Every write invalidates the whole `seo` namespace,** because a single flip moves a row, the
 * tier totals and the headroom at once, and the meter is read from `/budget`, not from the row.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type SeoRegistryRow = components['schemas']['SeoRegistryRow'];
export type SeoBudget = components['schemas']['SeoBudgetOut'];
export type SeoTier = 'prerendered' | 'edge' | 'excluded';
export type SeoComposition = components['schemas']['SeoCompositionRow'];

/**
 * The budget dashboard's drill-down into the registry (doc 05-admin/23 §3.5: "everything → §3.4
 * filtered"). It lands as the registry's own URL filters, `?seo_tab=&seo_type=`, so the drilled
 * view is a link like any other and the registry's "All" tab clears it.
 */
export interface SeoRegistryFilter {
  tab?: SeoTier | 'pinned';
  pageType?: string;
}

/** The registry's tab keys, in tab-bar order; `all` is the default and writes no param. */
export const SEO_TAB_KEYS = ['all', 'prerendered', 'edge', 'excluded', 'pinned'] as const;

/** The search params a drill-down writes, beside whatever shell keys the URL already carries. */
export function drillSearch(filter: SeoRegistryFilter): Record<string, string> {
  const out: Record<string, string> = {};
  if (filter.tab) out.seo_tab = filter.tab;
  if (filter.pageType) out.seo_type = filter.pageType;
  return out;
}
type RegistryList = components['schemas']['SeoRegistryListResponse'];
type TierOut = components['schemas']['SeoTierOut'];
type BoostOut = components['schemas']['SeoBoostOut'];
type RegenerateOut = components['schemas']['SeoSitemapRegenerateOut'];
type RebuildOut = components['schemas']['SeoSiteRebuildOut'];

export interface RegistryQuery extends Record<string, unknown> {
  page: number;
  page_size: number;
  tier?: SeoTier;
  page_type?: string;
  q?: string;
  locked?: boolean;
}

/** The query string, with absent filters left out rather than sent empty. */
export function registrySearch(params: RegistryQuery): string {
  const out = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') out.set(key, String(value));
  }
  return out.toString();
}

/** Filtered and paged on the server: tens of thousands of URLs do not fit in a browser tab. */
export function useRegistry(params: RegistryQuery) {
  return useQuery({
    queryKey: keys.seo.registry(params),
    queryFn: () =>
      authedClient.request<RegistryList>(`/v1/admin/seo/registry?${registrySearch(params)}`),
  });
}

export function useSeoBudget() {
  return useQuery({
    queryKey: keys.seo.budget(),
    queryFn: () => authedClient.request<SeoBudget>('/v1/admin/seo/budget'),
  });
}

function useSeoMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>) {
  const client = useQueryClient();
  return useMutation<TData, unknown, TArgs>({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.seo.all() }),
  });
}

/**
 * Promote, demote, exclude, pin or unpin. A tier change with `locked` omitted pins the row so the
 * nightly job cannot undo it; a pinned row must be unpinned in a call of its own first.
 */
export function useSetTier() {
  return useSeoMutation(
    ({ id, ...body }: { id: number; tier?: SeoTier; locked?: boolean; reason: string }) =>
      authedClient.request<TierOut>(`/v1/admin/seo/registry/${id}/tier`, { method: 'POST', body }),
  );
}

/** Ops' lift, 0–100, read by the next scoring run. It moves no tier by itself. */
export function useSetBoost() {
  return useSeoMutation(({ id, boost, reason }: { id: number; boost: number; reason: string }) =>
    authedClient.request<BoostOut>(`/v1/admin/seo/registry/${id}/boost`, {
      method: 'POST',
      body: { boost, reason },
    }),
  );
}

/** The same lift on every page of one page type — a festival season, a launch push. */
export function useSetFamilyBoost() {
  return useSeoMutation(
    ({ pageType, boost, reason }: { pageType: string; boost: number; reason: string }) =>
      authedClient.request<BoostOut>('/v1/admin/seo/registry/boost', {
        method: 'POST',
        body: { page_type: pageType, boost, reason },
      }),
  );
}

/** Queued, not run inline; `queued: false` means the worker would not take it. */
export function useRegenerateSitemaps() {
  return useSeoMutation((reason: string) =>
    authedClient.request<RegenerateOut>('/v1/admin/seo/sitemaps/regenerate', {
      method: 'POST',
      body: { reason },
    }),
  );
}

/** Job 19, forced (admin doc 23 §2): export, budget assertion, deploy, purge — queued, audited. */
export function useRebuildSite() {
  return useSeoMutation((reason: string) =>
    authedClient.request<RebuildOut>('/v1/admin/seo/site/rebuild', {
      method: 'POST',
      body: { reason },
    }),
  );
}

// ── Templates and per-page meta (doc 05-admin/23 §3.3, backend 40 §4.6) ─────────────────────

export type SeoTemplateRow = components['schemas']['SeoTemplateOut'];
export type SeoTemplateList = components['schemas']['SeoTemplateListResponse'];
export type SeoTemplateIn = components['schemas']['SeoTemplateIn'];
export type SeoTemplatePatch = components['schemas']['SeoTemplatePatch'];
export type SeoTemplatePreview = components['schemas']['SeoTemplatePreviewOut'];
export type SeoPageMeta = components['schemas']['SeoPageMetaOut'];
export type SeoPageMetaIn = components['schemas']['SeoPageMetaIn'];

/**
 * Every template, with the tokens each page type can fill. The server is the only judge of a
 * template — the palette greys out what it would refuse, and the save still asks it.
 */
export function useSeoTemplates(params: { page_type?: string; locale?: string } = {}) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) search.set(key, value);
  const query = search.toString();
  return useQuery({
    queryKey: keys.seo.templates(params),
    queryFn: () =>
      authedClient.request<SeoTemplateList>(`/v1/admin/seo/templates${query ? `?${query}` : ''}`),
  });
}

export function useCreateSeoTemplate() {
  return useSeoMutation((body: SeoTemplateIn) =>
    authedClient.request<SeoTemplateRow>('/v1/admin/seo/templates', { method: 'POST', body }),
  );
}

/** Edit or switch off. Switching off is refused while published pages render through it. */
export function useUpdateSeoTemplate() {
  return useSeoMutation(({ id, patch }: { id: number; patch: SeoTemplatePatch }) =>
    authedClient.request<SeoTemplateRow>(`/v1/admin/seo/templates/${id}`, {
      method: 'PATCH',
      body: patch,
    }),
  );
}

/** A read, sent as POST because it carries sample values. It writes nothing, so it invalidates nothing. */
export function usePreviewSeoTemplate() {
  return useMutation<SeoTemplatePreview, unknown, { id: number; values: Record<string, string> }>({
    mutationFn: ({ id, values }) =>
      authedClient.request<SeoTemplatePreview>(`/v1/admin/seo/templates/${id}/preview`, {
        method: 'POST',
        body: { values },
      }),
  });
}

export function usePageMeta(cmsPageId: number) {
  return useQuery({
    queryKey: keys.seo.meta(cmsPageId),
    queryFn: () => authedClient.request<SeoPageMeta>(`/v1/admin/seo/pages/${cmsPageId}/meta`),
  });
}

/** The page's whole override set. An empty body clears it and the template governs again. */
export function usePutPageMeta() {
  return useSeoMutation(({ cmsPageId, body }: { cmsPageId: number; body: SeoPageMetaIn }) =>
    authedClient.request<SeoPageMeta>(`/v1/admin/seo/pages/${cmsPageId}/meta`, {
      method: 'PUT',
      body,
    }),
  );
}
